import { Flex, Box, Text } from '@chakra-ui/react';
import {
  UnionChatMessage,
  InlineFile,
} from '@texas/api/endpoints/chat/messageTypes';
import { Session } from '@texas/api/endpoints/sessionApi';
import { ImageLoadingState } from '@texas/components/shared/image/ImageLoadingState';
import {
  fadeInRightAnimation,
  fadeInLeftAnimation,
} from '@texas/resources/animations/animations';
import ReactMarkdown from 'react-markdown';
import { AiChatMessageChunkRenderer } from '../AiChatMessageChunkRenderer';
import { UserChatMessageChunkRenderer } from '../UserChatMessageChunkRenderer';
import { ExpandImage } from '@texas/components/shared/image/ExpandImage';

export function ChatMessage({
  message,
  session,
}: {
  message: UnionChatMessage;
  session: Session;
}) {
  if (message.role === 'User') {
    if (session.currentUser.id === message.userId) {
      return (
        <Flex
          w="fit-content"
          ml="auto"
          flexDir="column"
          gap={2}
          alignItems="end"
          animation={fadeInRightAnimation()}
        >
          <MessageFiles files={message.files} />
          <Box
            bg="whiteAlpha.100"
            _light={{ bg: 'blackAlpha.100' }}
            p={2}
            borderRadius="md"
            w="fit-content"
          >
            {message.richTextChunks.length === 0 ? (
              <ReactMarkdown>{message.rawMessage}</ReactMarkdown>
            ) : (
              <UserChatMessageChunkRenderer chunks={message.richTextChunks} />
            )}
          </Box>
        </Flex>
      );
    } else {
      return (
        <Flex
          w="fit-content"
          ml="auto"
          flexDir="column"
          gap={2}
          alignItems="end"
          animation={fadeInRightAnimation()}
        >
          <MessageFiles files={message.files} />
          <Box
            bg="whiteAlpha.100"
            _light={{ bg: 'blackAlpha.100' }}
            p={2}
            borderRadius="md"
            w="fit-content"
          >
            {message.richTextChunks.length === 0 ? (
              <ReactMarkdown>{message.rawMessage}</ReactMarkdown>
            ) : (
              <UserChatMessageChunkRenderer chunks={message.richTextChunks} />
            )}
          </Box>
        </Flex>
      );
    }
    // Disabling since we do have other roles, but they are not used in the client
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (message.role === 'Assistant') {
    return (
      <Flex flexDir="column" gap={2} animation={fadeInLeftAnimation()}>
        <MessageFiles files={message.files} />
        {message.chunks.length === 0 ? (
          <Text>{message.rawMessage}</Text>
        ) : (
          <AiChatMessageChunkRenderer chunks={message.chunks} />
        )}
      </Flex>
    );
  }

  return null;
}

function MessageFiles({ files }: { files: InlineFile[] | null }) {
  if (!files || files.length === 0) {
    return null;
  }

  return (
    <>
      {files.map((file) => {
        if (!file.previewIdentifier) {
          return null;
        }

        return (
          <Box
            key={file.fileId}
            border="1px solid"
            borderColor="texas.bg.700"
            _light={{ borderColor: 'gray.100' }}
          >
            <ExpandImage variant="scale" identifier={file.previewIdentifier}>
              <ImageLoadingState
                identifier={file.previewIdentifier}
                maxH="250px"
                maxW="250px"
                size={250}
              />
            </ExpandImage>
          </Box>
        );
      })}
    </>
  );
}
